import { apiClient } from './apiClient';

// =============================================================================
// Types and Enums
// =============================================================================

export enum QuestionType {
  QA_CHECK = 'qa_check',
  EXTRACTION = 'extraction',
  CONDITIONAL = 'conditional',
  VALIDATION = 'validation',
}

export enum QuestionScope {
  TENANT = 'tenant',
  SYSTEM = 'system',
  SITE = 'site',
}

export enum QAResultStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  REVIEWED = 'reviewed',
}

// =============================================================================
// Question Set Types
// =============================================================================

export interface QuestionSet {
  id: string;
  tenant_id: string | null;
  site_id: string | null;
  name: string;
  description: string | null;
  scope: QuestionScope;
  document_type: string | null;
  document_labels: string[] | null;
  version: string | null;
  is_active: boolean;
  parent_set_id: string | null;
  external_reference: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuestionSetDetail extends QuestionSet {
  question_count: number;
  questions: Question[];
}

export interface QuestionSetListResponse {
  total: number;
  limit: number;
  offset: number;
  items: QuestionSet[];
}

export interface QuestionSetCreate {
  name: string;
  description?: string | null;
  scope?: QuestionScope;
  document_type?: string | null;
  document_labels?: string[] | null;
  version?: string | null;
  site_id?: string | null;
  parent_set_id?: string | null;
  external_reference?: string | null;
}

export interface QuestionSetUpdate {
  name?: string | null;
  description?: string | null;
  is_active?: boolean | null;
}

// =============================================================================
// Question Types
// =============================================================================

export interface Question {
  id: string;
  tenant_id: string; // Questions are per tenant
  question_set_id?: string; // Optional for question bank questions
  question_code?: string; // Optional for question bank questions
  question_text?: string; // Optional for question bank questions
  check_description: string; // From question bank API
  qc_type: string;
  category: string;
  first_appears_at: string;
  applicable_sheet_types: string[];
  validators: string[];
  cross_reference_sheets: string[];
  validation_trigger: string;
  meta: Record<string, any>;
  question_type?: QuestionType;
  expected_format?: string | null;
  enum_options?: string[] | null;
  display_order?: number;
  is_required?: boolean;
  weight?: number;
  help_text?: string | null;
  parent_question_id?: string | null;
  validation_rules?: Record<string, any> | null;
  condition_logic?: Record<string, any> | null;
  sheet_types?: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface QuestionCreate {
  question_code: string;
  question_text: string;
  question_type: QuestionType;
  expected_format?: string | null;
  enum_options?: string[] | null;
  display_order?: number;
  category?: string | null;
  is_required?: boolean;
  weight?: number;
  help_text?: string | null;
  parent_question_id?: string | null;
  validation_rules?: Record<string, any> | null;
  condition_logic?: Record<string, any> | null;
  sheet_types?: string[] | null;
}

// =============================================================================
// QA Result Types
// =============================================================================

export interface QAResult {
  id: string;
  tenant_id: string;
  document_id: string;
  question_set_id: string;
  status: QAResultStatus;
  completion_percentage: number | null;
  total_questions: number;
  answered_questions: number;
  passed_count: number | null;
  failed_count: number | null;
  warning_count: number | null;
  overall_score: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_outcome: string | null;
  created_at: string;
  updated_at: string;
}

export interface QAResultDetail extends QAResult {
  question_set: QuestionSet | null;
  answers: QuestionAnswer[];
}

export interface QAResultListResponse {
  total: number;
  limit: number;
  offset: number;
  items: QAResult[];
}

export interface QAResultCreate {
  question_set_id: string;
}

// =============================================================================
// Answer Types
// =============================================================================

export interface QuestionAnswer {
  id: string;
  qa_result_id: string;
  question_id: string;
  answer_value: string | null;
  answer_type: string | null;
  status: string | null;
  confidence: number | null;
  extraction_method: string | null;
  bounding_boxes: Record<string, any>[] | null;
  evidence_text: string | null;
  page_numbers: number[] | null;
  flagged: boolean;
  manually_reviewed: boolean;
  created_at: string;
  updated_at: string;
}

export interface AnswerCreate {
  question_id: string;
  answer_value?: string | null;
  confidence?: number | null;
  bounding_boxes?: Record<string, any>[] | null;
  evidence_text?: string | null;
  page_numbers?: number[] | null;
}

export interface AutoAnswerResponse {
  qa_result_id: string;
  answers_created: number;
  answers_updated: number;
  questions_skipped: number;
}

export interface QAReviewRequest {
  review_outcome: string;
  review_notes?: string | null;
}

// =============================================================================
// Query Parameters
// =============================================================================

export interface ListQuestionSetsParams {
  site_id?: string | null;
  document_type?: string | null;
  include_system?: boolean;
  active_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListQAResultsParams {
  question_set_id?: string | null;
  status?: QAResultStatus | null;
  limit?: number;
  offset?: number;
}

export interface ListDocumentQAResultsParams {
  document_id: string;
  status?: QAResultStatus | null;
  limit?: number;
  offset?: number;
}

export interface ListQuestionsParams {
  limit?: number;
  offset?: number;
  category?: string | null;
  qc_type?: string | null;
  tenant_id?: string;
}

export interface QuestionListResponse {
  items: Question[];
  total: number;
}

export interface QuestionFilterValues {
  categories: string[];
  qc_types: string[];
  first_appears_at: string[];
  validation_triggers: string[];
  applicable_sheet_types: string[];
  validators: string[];
  cross_reference_sheets: string[];
}

export interface QuestionBankCreate {
  check_description: string;
  qc_type: string;
  category: string;
  first_appears_at: string;
  applicable_sheet_types?: string[];
  validators?: string[];
  cross_reference_sheets?: string[];
  validation_trigger: string;
  meta?: Record<string, any>;
}

export interface QuestionBankUpdate {
  check_description?: string;
  qc_type?: string;
  category?: string;
  first_appears_at?: string;
  applicable_sheet_types?: string[];
  validators?: string[];
  cross_reference_sheets?: string[];
  validation_trigger?: string;
  meta?: Record<string, any>;
}

export interface PageWithBbox {
  page_number: number;
  bounding_box: { x_min?: number; y_min?: number; x_max?: number; y_max?: number } | null;
  description?: string | null;
  answer?: string | null;
}

export interface QuestionSummaryItem {
  question_id: string;
  check_description: string;
  qc_type: string;
  category: string;
  total_passes: number;
  total_failures: number;
  total_ca: number;
  pages_with_passes: number[];
  pages_with_failures: number[];
  pages_with_ca: number[];
  pass_page_details?: PageWithBbox[];
  fail_page_details?: PageWithBbox[];
  ca_page_details?: PageWithBbox[];
}

export interface QuestionSummaryResponse {
  items: QuestionSummaryItem[];
  total: number;
  total_with_responses: number;
  questions_with_failures: number;
  questions_all_pass: number;
}

export interface NomenclatureDeviationItem {
  equipment_type: string;
  label: string;
  issue: string;
  page_number: number;
  expected?: string;
  actual?: string;
  /** Bounding box for As-Built highlight (e.g. { x_min, y_min, x_max, y_max }). */
  bounding_box?: { x_min?: number; y_min?: number; x_max?: number; y_max?: number } | null;
}

export interface NomenclatureDeviationsResponse {
  items: NomenclatureDeviationItem[];
  total: number;
  equipment_type_counts: Record<string, number>;
}

export interface TitleblockInconsistencyItem {
  field: string;
  values: Array<{ value: string; pages: number[] }>;
}

export interface CrossPageQuestionItem {
  question_id: string;
  check_description: string;
  category: string;
  answer_type: string; // 'pass', 'fail', 'ca'
  description?: string;
  confidence: number;
  page_number: number;
  pages_referenced: number[];
  applicable_sheet_types: string[];
  /** Bounding box for As-Built highlight (for this response's page_number). */
  bounding_box?: { x_min?: number; y_min?: number; x_max?: number; y_max?: number } | null;
}

export interface CrossPageQuestionGroup {
  category: string;
  questions: CrossPageQuestionItem[];
}

export interface PageReferenceItem {
  reference_text: string;
  pages: number[];
}

export interface PageReferenceValidationItem {
  reference_text: string;
  source_page: number;
  is_valid: boolean;
  target_found_in_toc: boolean;
  target_page: number | null;
  validation_description: string | null;
  confidence: number;
}

export interface PageReferenceValidationGroup {
  page_number: number;
  sheet_id: string | null;
  total_refs: number;
  valid_refs: number;
  invalid_refs: number;
  references: PageReferenceValidationItem[];
}

export interface PageReferenceValidationResponse {
  total_references: number;
  total_pages: number;
  total_valid: number;
  total_invalid: number;
  pages: PageReferenceValidationGroup[];
}

export interface CrossPageValidationResponse {
  titleblock_inconsistencies: TitleblockInconsistencyItem[];
  titleblock_inconsistency_count: number;
  failed_questions: CrossPageQuestionGroup[];
  passed_questions: CrossPageQuestionGroup[];
  ca_questions: CrossPageQuestionGroup[];
  no_response_questions: Array<{ question_id: string; check_description: string }>;
  total_failed: number;
  total_passed: number;
  total_ca: number;
  total_no_response: number;
  page_references: PageReferenceItem[];
}

// =============================================================================
// New QA Result API (from qa_results table)
// =============================================================================

export interface QAResultNew {
  id: string;
  tenant_id: string;
  site_id: string | null;
  pdf_filename: string;
  checklist_name: string;
  checklist_version: string;
  status: string;
  summary: Record<string, any>;
  toc_data: Record<string, any>;
  meta: Record<string, any>;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface QAResultListResponseNew {
  items: QAResultNew[];
  total: number;
}

export interface QAExecutiveSummary {
  qa_result_id: string;
  pdf_filename: string;
  checklist_name: string;
  checklist_version: string;
  status: string;  // Status: pending, processing, completed, failed
  total_pages: number;
  total_questions: number;
  total_answered: number;
  total_passed: number;
  total_failed: number;
  total_unanswerable: number;
  pass_rate: number;
  category_breakdown: Array<{
    category: string;
    total: number;
    pass: number;
    fail: number;
    ca: number;
    pass_rate: number;
  }>;
  results_by_page: Record<number, {
    total: number;
    pass: number;
    fail: number;
    ca: number;
  }>;
}

export interface PageSummaryItem {
  page_number: number;
  sheet_id?: string | null;
  sheet_title?: string | null;
  sheet_types: string[];
  summary: {
    pass: number;
    fail: number;
    ca: number;
    total: number;
  };
}

export interface PageListResponse {
  qa_result_id: string;
  total_pages: number;
  pages: PageSummaryItem[];
  sheet_type_counts: Record<string, number>;
}

export interface PageDetailResponseQuestion {
  id: string | null;
  check_description: string | null;
  category: string | null;
  qc_type: string | null;
  validation_trigger: string | null;
  cross_reference_sheets: string[];
  applicable_sheet_types: string[];
  difficulty?: string | null;
}

export interface PageDetailResponseItem {
  id: string;
  qa_result_id: string;
  qa_sheet_result_id?: string | null;
  question_id: string;
  response_type: string;
  answer: boolean;
  confidence: number;
  page_number: number;
  description?: string | null;
  answer_type: string;
  bounding_box?: Record<string, any> | null;
  pages_referenced?: number[] | null;
  relevant_pages?: number[] | null;
  meta: Record<string, any>;
  created_at?: string | null;
  question?: PageDetailResponseQuestion | null;
  category: string;
}

export interface PageReferenceValidationDetail {
  id: string;
  reference: {
    source_page: number;
    reference_text: string;
    target_sheet_id: string;
    reference_type: string;
    bounding_box?: Record<string, any>;
  };
  is_valid: boolean;
  target_found_in_toc: boolean;
  target_page: number | null;
  validation_description: string | null;
  confidence: number;
}

export interface PageDetailResponse {
  qa_result_id: string;
  page_number: number;
  sheet_id?: string | null;
  sheet_title?: string | null;
  sheet_number?: string | null;
  sheet_types: string[];
  titleblock?: Record<string, any> | null;
  equipment_labels: Record<string, any>;
  page_references: any[];  // Basic references from extraction
  page_reference_validations: PageReferenceValidationDetail[];  // Validation results with details
  nomenclature_deviations: any[];
  responses: PageDetailResponseItem[];
  summary: {
    pass: number;
    fail: number;
    ca: number;
    total: number;
  };
}

export interface ListQAResultsParams {
  site_id?: string | null;
  limit?: number;
  offset?: number;
}

export interface GetQuestionSetParams {
  set_id: string;
  include_questions?: boolean;
}

export interface GetQAResultParams {
  result_id: string;
  include_answers?: boolean;
}

export interface QAStatistics {
  total_results: number;
  by_status: Record<QAResultStatus, number>;
  by_question_set: Record<string, number>;
  average_completion: number;
  average_score: number | null;
}

// =============================================================================
// RTK Query API
// =============================================================================

export const qaApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    // Question Set Endpoints
    listQuestionSets: builder.query<QuestionSetListResponse, ListQuestionSetsParams | void>({
      query: (params = {}) => ({
        url: '/api/v1/qa/question-sets',
        method: 'GET',
        params: {
          site_id: params.site_id,
          document_type: params.document_type,
          include_system: params.include_system ?? true,
          active_only: params.active_only ?? true,
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      }),
      providesTags: ['QA'],
    }),

    getQuestionSet: builder.query<QuestionSetDetail, GetQuestionSetParams>({
      query: ({ set_id, include_questions = true }) => ({
        url: `/api/v1/qa/question-sets/${set_id}`,
        method: 'GET',
        params: {
          include_questions,
        },
      }),
      providesTags: (result, error, arg) => [{ type: 'QA', id: `question-set-${arg.set_id}` }],
    }),

    createQuestionSet: builder.mutation<QuestionSet, QuestionSetCreate>({
      query: (body) => ({
        url: '/api/v1/qa/question-sets',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['QA'],
    }),

    createSystemQuestionSet: builder.mutation<QuestionSet, QuestionSetCreate>({
      query: (body) => ({
        url: '/api/v1/qa/question-sets/system',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['QA'],
    }),

    updateQuestionSet: builder.mutation<QuestionSet, { set_id: string; data: QuestionSetUpdate }>({
      query: ({ set_id, data }) => ({
        url: `/api/v1/qa/question-sets/${set_id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        'QA',
        { type: 'QA', id: `question-set-${arg.set_id}` },
      ],
    }),

    deleteQuestionSet: builder.mutation<void, string>({
      query: (set_id) => ({
        url: `/api/v1/qa/question-sets/${set_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['QA'],
    }),

    // Question Endpoints
    addQuestion: builder.mutation<Question, { set_id: string; data: QuestionCreate }>({
      query: ({ set_id, data }) => ({
        url: `/api/v1/qa/question-sets/${set_id}/questions`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        'QA',
        { type: 'QA', id: `question-set-${arg.set_id}` },
      ],
    }),

    listQuestions: builder.query<Question[], { set_id: string; category?: string | null }>({
      query: ({ set_id, category }) => ({
        url: `/api/v1/qa/question-sets/${set_id}/questions`,
        method: 'GET',
        params: {
          category: category || undefined,
        },
      }),
      providesTags: (result, error, arg) => [
        { type: 'QA', id: `question-set-${arg.set_id}-questions` },
      ],
    }),

    // QA Result Endpoints
    startQASession: builder.mutation<QAResult, { document_id: string; data: QAResultCreate }>({
      query: ({ document_id, data }) => ({
        url: `/api/v1/qa/documents/${document_id}/qa-results`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['QA'],
    }),

    listDocumentQAResults: builder.query<
      QAResultListResponse,
      ListDocumentQAResultsParams
    >({
      query: ({ document_id, status, limit = 50, offset = 0 }) => ({
        url: `/api/v1/qa/documents/${document_id}/qa-results`,
        method: 'GET',
        params: {
          status: status || undefined,
          limit,
          offset,
        },
      }),
      providesTags: (result, error, arg) => [
        { type: 'QA', id: `document-${arg.document_id}-results` },
      ],
    }),

    listQAResults: builder.query<QAResultListResponse, ListQAResultsParams | void>({
      query: (params = {}) => ({
        url: '/api/v1/qa/qa-results',
        method: 'GET',
        params: {
          question_set_id: params.question_set_id,
          status: params.status,
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      }),
      providesTags: ['QA'],
    }),

    getQAResult: builder.query<QAResultDetail, GetQAResultParams>({
      query: ({ result_id, include_answers = true }) => ({
        url: `/api/v1/qa/qa-results/${result_id}`,
        method: 'GET',
        params: {
          include_answers,
        },
      }),
      providesTags: (result, error, arg) => [{ type: 'QA', id: `qa-result-${arg.result_id}` }],
    }),

    answerQuestion: builder.mutation<
      { id: string; status: string },
      { result_id: string; data: AnswerCreate }
    >({
      query: ({ result_id, data }) => ({
        url: `/api/v1/qa/qa-results/${result_id}/answers`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'QA', id: `qa-result-${arg.result_id}` },
      ],
    }),

    autoAnswerQuestions: builder.mutation<
      AutoAnswerResponse,
      { result_id: string; answers: AnswerCreate[] }
    >({
      query: ({ result_id, answers }) => ({
        url: `/api/v1/qa/qa-results/${result_id}/auto-answer`,
        method: 'POST',
        body: answers,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'QA', id: `qa-result-${arg.result_id}` },
      ],
    }),

    flagAnswer: builder.mutation<
      { id: string; flagged: boolean },
      { result_id: string; question_id: string; reason: string }
    >({
      query: ({ result_id, question_id, reason }) => ({
        url: `/api/v1/qa/qa-results/${result_id}/flag/${question_id}`,
        method: 'POST',
        params: {
          reason,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'QA', id: `qa-result-${arg.result_id}` },
      ],
    }),

    reviewQAResult: builder.mutation<
      QAResult,
      { result_id: string; data: QAReviewRequest }
    >({
      query: ({ result_id, data }) => ({
        url: `/api/v1/qa/qa-results/${result_id}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        'QA',
        { type: 'QA', id: `qa-result-${arg.result_id}` },
      ],
    }),

    triggerQAExtraction: builder.mutation<
      { task_id: string; backend: string; queue: string; metadata: Record<string, any> },
      { site_id: string; options?: Record<string, any> }
    >({
      query: (data) => ({
        url: '/api/v1/qa-results/trigger-extraction',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['QA'],
    }),

    getQAStatistics: builder.query<QAStatistics, { question_set_id?: string | null } | void>({
      query: (params = {}) => ({
        url: '/api/v1/qa/statistics',
        method: 'GET',
        params: {
          question_set_id: params.question_set_id || undefined,
        },
      }),
      providesTags: ['QA'],
    }),

    // Question Bank Endpoints (new API)
    listQuestionBankQuestions: builder.query<QuestionListResponse, ListQuestionsParams | void>({
      query: (params = {}) => ({
        url: '/api/v1/question-banks/questions',
        method: 'GET',
        params: {
          limit: params.limit ?? 100,
          offset: params.offset ?? 0,
          category: params.category || undefined,
          qc_type: params.qc_type || undefined,
          tenant_id: params.tenant_id || undefined,
        },
      }),
      providesTags: ['QuestionBank'],
    }),

    getQuestionBankQuestion: builder.query<Question, string>({
      query: (question_id) => ({
        url: `/api/v1/question-banks/questions/${question_id}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [{ type: 'QuestionBank', id: `question-${arg}` }],
    }),

    getQuestionBankFilterValues: builder.query<QuestionFilterValues, { tenant_id?: string } | void>({
      query: (params = {}) => ({
        url: '/api/v1/question-banks/questions/filter-values',
        method: 'GET',
        params: {
          tenant_id: params.tenant_id || undefined,
        },
      }),
      providesTags: ['QuestionBank'],
    }),

    createQuestionBankQuestion: builder.mutation<Question, QuestionBankCreate & { tenant_id?: string }>({
      query: ({ tenant_id, ...body }) => ({
        url: '/api/v1/question-banks/questions',
        method: 'POST',
        body,
        params: {
          tenant_id: tenant_id || undefined,
        },
      }),
      invalidatesTags: ['QuestionBank'],
    }),

    updateQuestionBankQuestion: builder.mutation<Question, { question_id: string; body: QuestionBankUpdate; tenant_id?: string }>({
      query: ({ question_id, body, tenant_id }) => ({
        url: `/api/v1/question-banks/questions/${question_id}`,
        method: 'PATCH',
        body,
        params: {
          tenant_id: tenant_id || undefined,
        },
      }),
      invalidatesTags: ['QuestionBank'],
    }),

    deleteQuestionBankQuestion: builder.mutation<void, { question_id: string; tenant_id?: string }>({
      query: ({ question_id, tenant_id }) => ({
        url: `/api/v1/question-banks/questions/${question_id}`,
        method: 'DELETE',
        params: {
          tenant_id: tenant_id || undefined,
        },
      }),
      invalidatesTags: ['QuestionBank'],
    }),

    getQuestionSummary: builder.query<QuestionSummaryResponse, { site_id?: string; limit?: number; offset?: number } | void>({
      query: (params = {}) => ({
        url: '/api/v1/question-banks/questions/summary',
        method: 'GET',
        params: {
          site_id: params.site_id || undefined,
          limit: params.limit || 10,
          offset: params.offset || 0,
        },
      }),
      providesTags: ['QuestionBank'],
    }),

    getNomenclatureDeviations: builder.query<NomenclatureDeviationsResponse, { site_id?: string } | void>({
      query: (params = {}) => ({
        url: '/api/v1/nomenclature-deviations',
        method: 'GET',
        params: {
          site_id: params.site_id || undefined,
        },
      }),
      providesTags: ['QA'],
    }),

    getCrossPageValidation: builder.query<CrossPageValidationResponse, { site_id?: string } | void>({
      query: (params = {}) => ({
        url: '/api/v1/cross-page-validation',
        method: 'GET',
        params: {
          site_id: params.site_id || undefined,
        },
      }),
      providesTags: ['QA'],
    }),

    getPageReferenceValidations: builder.query<PageReferenceValidationResponse, { site_id?: string } | void>({
      query: (params = {}) => ({
        url: '/api/v1/page-reference-validations',
        method: 'GET',
        params: {
          site_id: params.site_id || undefined,
        },
      }),
      providesTags: ['QA'],
    }),

    // New QA Result Endpoints
    listQAResultsNew: builder.query<QAResultListResponseNew, ListQAResultsParams | void>({
      query: (params = {}) => ({
        url: '/api/v1/qa-results',
        method: 'GET',
        params: {
          site_id: params.site_id || undefined,
          limit: params.limit ?? 100,
          offset: params.offset ?? 0,
        },
      }),
      providesTags: ['QAResult'],
    }),

    getQAResultExecutiveSummary: builder.query<QAExecutiveSummary, string>({
      query: (qa_result_id) => ({
        url: `/api/v1/qa-results/${qa_result_id}/executive-summary`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [{ type: 'QAResult', id: `executive-summary-${arg}` }],
    }),

    // Page List API - Get all pages with filtering
    getQAResultPages: builder.query<PageListResponse, { qa_result_id: string; sheet_types?: string[] }>({
      query: ({ qa_result_id, sheet_types }) => {
        const params: Record<string, any> = {};
        // FastAPI expects multiple query params with same name for lists
        if (sheet_types && sheet_types.length > 0) {
          // RTK Query will handle array params correctly
          params.sheet_types = sheet_types;
        }
        return {
          url: `/api/v1/qa-results/${qa_result_id}/pages`,
          method: 'GET',
          params,
        };
      },
      providesTags: (result, error, arg) => [{ type: 'QAResult', id: `pages-${arg.qa_result_id}` }],
    }),

    // Page Detail API - Get detailed content for a specific page
    getQAResultPageDetail: builder.query<PageDetailResponse, { qa_result_id: string; page_number: number }>({
      query: ({ qa_result_id, page_number }) => ({
        url: `/api/v1/qa-results/${qa_result_id}/pages/${page_number}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => [
        { type: 'QAResult', id: `page-${arg.qa_result_id}-${arg.page_number}` },
      ],
    }),

  }),
});

export const {
  useListQuestionSetsQuery,
  useGetQuestionSetQuery,
  useCreateQuestionSetMutation,
  useCreateSystemQuestionSetMutation,
  useUpdateQuestionSetMutation,
  useDeleteQuestionSetMutation,
  useAddQuestionMutation,
  useListQuestionsQuery,
  useStartQASessionMutation,
  useListDocumentQAResultsQuery,
  useListQAResultsQuery,
  useGetQAResultQuery,
  useAnswerQuestionMutation,
  useAutoAnswerQuestionsMutation,
  useFlagAnswerMutation,
  useReviewQAResultMutation,
  useGetQAStatisticsQuery,
  useListQuestionBankQuestionsQuery,
  useGetQuestionBankQuestionQuery,
  useGetQuestionBankFilterValuesQuery,
  useCreateQuestionBankQuestionMutation,
  useUpdateQuestionBankQuestionMutation,
  useDeleteQuestionBankQuestionMutation,
  useGetQuestionSummaryQuery,
  useListQAResultsNewQuery,
  useGetQAResultExecutiveSummaryQuery,
  useGetNomenclatureDeviationsQuery,
  useGetCrossPageValidationQuery,
  useGetPageReferenceValidationsQuery,
  useGetQAResultPagesQuery,
  useGetQAResultPageDetailQuery,
  useTriggerQAExtractionMutation,
} = qaApi;
